import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';
import $api from '@/http';
import { auth } from '@/auth';
import { documentHub } from '@/signalr/hubs/DocumentHub';
import { AttachmentChangesMonitor } from '@services/attachmentChanges/AttachmentChangesMonitor';
import { isOwnAttachmentUpload } from '@services/attachmentChanges/attachmentUploadOrigins';
import type { IAttachmentChange, IAttachmentState } from '@services/attachmentChanges/attachmentChanges.types';
import type { IAttachmentLocateRequest } from '@/context/DocumentAttachmentChangesContext';
import AttachmentChangeToast from '@organisms/document/AttachmentChangeToast/AttachmentChangeToast';

export interface IAttachmentLocation {
    tableKey: string;
    tabKey: string;
}

export function useDocumentAttachmentChanges(documentId: string, enabled: boolean, locations: IAttachmentLocation[]) {
    const locationsRef = useRef(locations);
    locationsRef.current = locations;
    const [revision, setRevision] = useState<string>();
    const [request, setRequest] = useState<IAttachmentLocateRequest>();
    const [unread, setUnread] = useState<IAttachmentChange[]>([]);
    const snapshot = useRef<IAttachmentState | undefined>(undefined);
    const showRef = useRef<(changes: IAttachmentChange[], tableKey?: string) => void>(() => {});
    const sequence = useRef(0);
    const handled = useCallback((id: number) => setRequest((value) => (value?.id === id ? undefined : value)), []);
    const markViewed = useCallback((tableKey: string, contentToken: string, visibleKeys: string[]) => {
        if (contentToken !== snapshot.current?.stateToken) return;
        const viewed = (change: IAttachmentChange) =>
            change.item.tableKeys.includes(tableKey) && visibleKeys.includes(change.item.attachmentKey);
        setUnread((current) => (current.some(viewed) ? current.filter((change) => !viewed(change)) : current));
    }, []);

    useEffect(() => {
        setRevision(undefined);
        setRequest(undefined);
        setUnread([]);
        snapshot.current = undefined;
        if (!enabled || !/^\d+$/.test(documentId) || documentId === '0') return;
        const toastId = `attachment-changes:${documentId}`;
        let active = true;
        let shown = false;
        let unsubscribe = () => {};
        let pending = new Map<string, IAttachmentChange>();
        let displayed = new Map<string, IAttachmentChange>();
        let flushTimer: ReturnType<typeof setTimeout> | undefined;
        const close = () => {
            notifications.hide(toastId);
            shown = false;
            displayed.clear();
        };
        const locate = (changes: IAttachmentChange[], tableKey?: string) => {
            const location = locationsRef.current.find(
                (candidate) =>
                    (!tableKey || candidate.tableKey === tableKey) &&
                    changes.some((change) => change.item.tableKeys.includes(candidate.tableKey)),
            );
            if (!location || !active) return;
            const items = changes
                .filter((change) => change.item.tableKeys.includes(location.tableKey))
                .map((change) => change.item);
            setRequest({ id: ++sequence.current, ...location, items });
            close();
        };
        showRef.current = locate;
        const flush = () => {
            flushTimer = undefined;
            if (!active || (!pending.size && !shown) || document.visibilityState === 'hidden') return;
            if (document.querySelector('[role="dialog"][aria-modal="true"]')) {
                flushTimer = setTimeout(flush, 500);
                return;
            }
            for (const [key, change] of pending) displayed.set(key, change);
            pending.clear();
            const changes = [...displayed.values()].filter((change) =>
                locationsRef.current.some((location) => change.item.tableKeys.includes(location.tableKey)),
            );
            if (!changes.length) {
                close();
                return;
            }
            const message = (
                <AttachmentChangeToast
                    key={++sequence.current}
                    changes={changes}
                    onShow={() => locate(changes)}
                    onExpire={close}
                />
            );
            const options = {
                id: toastId,
                message,
                autoClose: false as const,
                withCloseButton: true,
                onClose: () => {
                    shown = false;
                    displayed.clear();
                },
            };
            if (shown) notifications.update(options);
            else notifications.show(options);
            shown = true;
        };
        const onHash = () => {
            if (document.visibilityState !== 'hidden') monitor.invalidate();
        };
        const monitor = new AttachmentChangesMonitor({
            documentId,
            fetchState: async (signal) =>
                (
                    await $api.get<IAttachmentState>(`document/${documentId}/attachment-state`, {
                        signal,
                        suppressErrorNotification: true,
                    })
                ).data,
            isOwn: isOwnAttachmentUpload,
            onHash: (hash) => documentHub.acknowledge(documentId, onHash, hash),
            onUnavailable: () => {
                unsubscribe();
                close();
                pending.clear();
                snapshot.current = undefined;
                setRevision(undefined);
                setUnread([]);
                setRequest(undefined);
            },
            onState: (state, changes, reset) => {
                if (!active) return;
                const oldToken = snapshot.current?.stateToken;
                snapshot.current = state;
                if (oldToken !== state.stateToken) setRevision(state.stateToken);
                const current = new Map(state.items.map((item) => [item.logicalFileId, item]));
                const retain = (change: IAttachmentChange): IAttachmentChange[] => {
                    const item = current.get(change.item.logicalFileId);
                    return item && !isOwnAttachmentUpload(item.originOperationId) ? [{ ...change, item }] : [];
                };
                if (reset) {
                    close();
                    pending.clear();
                    setUnread([]);
                } else {
                    pending = new Map(
                        [...pending.values()].flatMap(retain).map((change) => [change.item.logicalFileId, change]),
                    );
                    displayed = new Map(
                        [...displayed.values()].flatMap(retain).map((change) => [change.item.logicalFileId, change]),
                    );
                    if (shown && oldToken !== state.stateToken) {
                        for (const [key, change] of displayed) pending.set(key, change);
                        close();
                    }
                    setUnread((previous) => [
                        ...new Map(
                            [...previous.flatMap(retain), ...changes].map((change) => [
                                change.item.logicalFileId,
                                change,
                            ]),
                        ).values(),
                    ]);
                }
                for (const change of changes) pending.set(change.item.logicalFileId, change);
                if ((pending.size || (shown && oldToken !== state.stateToken)) && !flushTimer)
                    flushTimer = setTimeout(flush, 700);
            },
        });
        unsubscribe = documentHub.subscribe(documentId, '', onHash);
        const stopOnLogout = auth.subscribe(({ origin, accessToken }) => {
            if (origin !== 'login' && accessToken) return;
            monitor.stop();
            unsubscribe();
            close();
            pending.clear();
            snapshot.current = undefined;
            setUnread([]);
            setRequest(undefined);
            setRevision(undefined);
        });
        const wake = () => {
            if (document.visibilityState === 'hidden') return;
            monitor.invalidate();
            // Only flush after a fresh successful snapshot has removed stale/deleted files.
        };
        document.addEventListener('visibilitychange', wake);
        window.addEventListener('focus', wake);
        monitor.invalidate();
        return () => {
            active = false;
            monitor.stop();
            stopOnLogout();
            unsubscribe();
            clearTimeout(flushTimer);
            document.removeEventListener('visibilitychange', wake);
            window.removeEventListener('focus', wake);
            close();
            showRef.current = () => {};
        };
    }, [documentId, enabled]);

    const revealUnread = useCallback((tableKey: string) => showRef.current(unread, tableKey), [unread]);
    const context = useMemo(
        () => ({
            documentId,
            revision,
            items: snapshot.current?.items,
            request,
            handled,
            markViewed,
            unread: unread.map((change) => change.item),
            revealUnread,
        }),
        [documentId, revision, request, handled, markViewed, unread, revealUnread],
    );
    return { context, unread, revealUnread };
}
